import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

interface ResolvedAuthor {
  login: string
  databaseId: number
}

interface AssigneeNode {
  databaseId?: number
  login: string
}

async function fetchAuthorIds(owner: string, repo: string, logins: string[]): Promise<ResolvedAuthor[]> {
  const loginNames = logins.join(',')
  const params = new URLSearchParams({owner, repo, loginNames})
  const url = `/_ghui/item-pickers/assignees?${params.toString()}`

  const res = await verifiedFetchJSON(url, {method: 'GET'})
  if (!res.ok) return []

  const json = await res.json()
  const nodes = json.data?.repository?.suggestedActors?.nodes ?? []

  return nodes
    .filter((node: AssigneeNode): node is AssigneeNode & {databaseId: number} => node.databaseId != null)
    .map((node: AssigneeNode & {databaseId: number}) => ({login: node.login, databaseId: node.databaseId}))
}

/**
 * Resolves author logins to numeric database IDs via the assignees item picker endpoint.
 * Used to translate URL-based `?author=login` filters into `?creator_id=` params
 * that the CMC API actually supports.
 */
export function useResolveAuthorIds(owner: string, repo: string, authorLogins: string[] | undefined) {
  const enabled = !!authorLogins && authorLogins.length > 0

  const {data, isLoading} = useQuery({
    queryKey: ['resolve-author-ids', owner, repo, authorLogins],
    queryFn: () => fetchAuthorIds(owner, repo, authorLogins as string[]),
    enabled,
    staleTime: 5 * 60 * 1000,
  })

  const resolvedAuthorIds = useMemo(() => data?.map(a => a.databaseId), [data])

  if (!enabled) {
    return {resolvedAuthorIds: undefined, isResolvingAuthors: false}
  }

  return {
    resolvedAuthorIds,
    isResolvingAuthors: isLoading,
  }
}
